import { useSearchParams } from "react-router-dom";
import DashboardWrapper from "src/components/common/DashboardWrapper";
import {
  Box,
  Typography,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useForm } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { Input } from "src/components/common/FormComponents";
import { Button } from "src/components/common/FormComponents/Buttons";
import FileUpload from "src/components/common/FormComponents/FileUplaod";
import {
  getLandDetailsById,
  createMonitoringVisit,
  getMonitoringVisits,
  createMonitoringRemark,
  getMonitoringRemarks,
} from "src/utils/api/dashboard";
import { getFileByKey } from "src/utils/api/fileupload";
import { useState } from "react";

interface MonitoringVisitForm {
  periodFrom: string;
  periodTo: string;
  monitoringDate: string;
  monitoringPersonName: string;
  monitoringPersonDesignation: string;
  monitoringDocument: any[];
}

const sectionTitleStyle = {
  fontWeight: 700,
  fontSize: "1.25rem",
  color: "#1b4332",
  mb: 1,
};

const labelStyle = {
  fontWeight: 600,
  fontSize: "0.85rem",
  color: "#1b4332",
  mb: 0.5,
};

const valueStyle = {
  fontSize: "0.9rem",
  color: "#333",
};

const headerCellStyle = {
  fontWeight: 700,
  color: "#1b4332",
  fontSize: "0.9rem",
};

const MonitoringReport = () => {
  const [searchParams] = useSearchParams();
  const epid = searchParams.get("epid");
  const queryClient = useQueryClient();
  const [previewData, setPreviewData] = useState<any>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedVisit, setSelectedVisit] = useState<any>(null);
  const [remarkText, setRemarkText] = useState("");

  const {
    handleSubmit,
    control,
    setValue,
    getValues,
    setError,
    clearErrors,
    trigger,
    reset,
    formState: { errors },
  } = useForm<MonitoringVisitForm>({
    mode: "onChange",
    defaultValues: {
      periodFrom: "",
      periodTo: "",
      monitoringDate: new Date().toISOString().split("T")[0],
      monitoringPersonName: "",
      monitoringPersonDesignation: "",
      monitoringDocument: [],
    },
  });

  // Fetch land parcel details
  const { data: landData, isLoading: landLoading } = useQuery(
    ["landDetails", epid],
    () => getLandDetailsById(epid!),
    { enabled: !!epid }
  );

  // Fetch existing monitoring visits
  const { data: visitsData, isLoading: visitsLoading } = useQuery(
    ["monitoringVisits", epid],
    () => getMonitoringVisits(epid!),
    { enabled: !!epid }
  );

  // Submit monitoring visit
  const { mutate: submitVisit, isLoading: submitting } = useMutation(
    (data: any) => createMonitoringVisit(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["monitoringVisits", epid]);
        reset();
        setPreviewData(null);
      },
    }
  );

  // Fetch remarks for selected visit
  const { data: remarksData, isLoading: remarksLoading } = useQuery(
    ["monitoringRemarks", selectedVisit?.id],
    () => getMonitoringRemarks(selectedVisit!.id),
    { enabled: !!selectedVisit?.id }
  );

  // Post a new remark
  const { mutate: postRemark, isLoading: postingRemark } = useMutation(
    (data: { monitoringVisitId: string; remarks: string }) =>
      createMonitoringRemark(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["monitoringRemarks", selectedVisit?.id]);
        setRemarkText("");
      },
    }
  );

  const handleOpenModal = (visit: any) => {
    setSelectedVisit(visit);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedVisit(null);
    setRemarkText("");
  };

  const handlePostRemark = () => {
    if (!remarkText.trim() || !selectedVisit?.id) return;
    postRemark({
      monitoringVisitId: selectedVisit.id,
      remarks: remarkText.trim(),
    });
  };

  const remarks = remarksData?.data?.data || remarksData?.data || [];

  // Fetch signed URL for the selected visit's document
  const { data: fileUrlResponse } = useQuery(
    ["monitoringDocUrl", selectedVisit?.monitoringDocument],
    () => getFileByKey(selectedVisit!.monitoringDocument),
    { enabled: !!selectedVisit?.monitoringDocument && modalOpen }
  );

  const documentUrl =
    (fileUrlResponse as any)?.data?.data?.fileUrl ||
    (fileUrlResponse as any)?.data?.fileUrl ||
    null;

  const landInfo = landData?.data?.data || landData?.data;

  const onPreview = (data: MonitoringVisitForm) => {
    setPreviewData(data);
  };

  const onSubmit = () => {
    const data = previewData || getValues();
    const documentKey =
      data.monitoringDocument?.[0]?.key || data.monitoringDocument?.[0] || null;

    submitVisit({
      landOwnershipEpid: epid,
      projectId: landInfo?.projectDetailId,
      periodFrom: data.periodFrom,
      periodTo: data.periodTo,
      monitoringDate: data.monitoringDate,
      monitoringPersonName: data.monitoringPersonName,
      monitoringPersonDesignation: data.monitoringPersonDesignation,
      monitoringDocument: documentKey,
    });
  };

  const visits = visitsData?.data?.data || visitsData?.data || [];

  const formatPeriod = (from: string, to: string) => {
    if (!from || !to) return "-";
    const f = new Date(from);
    const t = new Date(to);
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    return `${months[f.getMonth()]} ${f.getFullYear()} - ${
      months[t.getMonth()]
    } ${t.getFullYear()}`;
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
  };

  return (
    <DashboardWrapper
      title={"Monitoring Report"}
      buttonIcon="/assets/svg/roundPlusIcon.svg"
      buttonText=""
      onButtonClick={() => {}}
      showBtn={false}
      showBackBtn={true}
    >
      <Box sx={{ padding: "20px", background: "white", borderRadius: "8px" }}>
        {/* Basic Land Parcel Detail Section */}
        <Typography sx={sectionTitleStyle}>Basic land parcel detail</Typography>
        <Divider sx={{ borderColor: "#1b4332", borderWidth: 1.5, mb: 2 }} />

        {landLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: 2,
              mb: 3,
            }}
          >
            <Box>
              <Typography sx={labelStyle}>Land Parcel Reg No</Typography>
              <Typography sx={valueStyle}>
                {landInfo?.projectDetail?.registrationNo || "-"}
              </Typography>
            </Box>
            <Box>
              <Typography sx={labelStyle}>Area Selected (Ha)</Typography>
              <Typography sx={valueStyle}>
                {landInfo?.totalArea != null
                  ? Number(landInfo.totalArea).toFixed(2)
                  : "-"}
              </Typography>
            </Box>
            <Box>
              <Typography sx={labelStyle}>EPID</Typography>
              <Typography sx={valueStyle}>{epid || "-"}</Typography>
            </Box>
            <Box>
              <Typography sx={labelStyle}>Entity Name</Typography>
              <Typography sx={valueStyle}>
                {landInfo?.user?.firstName || "-"}
              </Typography>
            </Box>
          </Box>
        )}

        <Divider sx={{ borderColor: "#1b4332", borderWidth: 1.5, mb: 2 }} />

        {/* GCP Monitoring Report Upload Form */}
        <Typography sx={sectionTitleStyle}>
          GCP Monitoring Report Upload
        </Typography>
        <Divider sx={{ borderColor: "#1b4332", borderWidth: 1.5, mb: 2 }} />

        <form onSubmit={handleSubmit(onPreview)}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: 2,
              mb: 2,
            }}
          >
            <Box>
              <Typography sx={labelStyle}>Period From</Typography>
              <Input
                name="periodFrom"
                control={control}
                errors={errors}
                label=""
                placeholder="dd-mm-yyyy"
                type="date"
                rules={{ required: "Period From is required" }}
              />
            </Box>
            <Box>
              <Typography sx={labelStyle}>Period To</Typography>
              <Input
                name="periodTo"
                control={control}
                errors={errors}
                label=""
                placeholder="dd-mm-yyyy"
                type="date"
                rules={{ required: "Period To is required" }}
              />
            </Box>
            <Box>
              <Typography sx={labelStyle}>Date of Monitoring</Typography>
              <Input
                name="monitoringDate"
                control={control}
                errors={errors}
                label=""
                placeholder="dd-mm-yyyy"
                type="date"
                rules={{ required: "Date of Monitoring is required" }}
              />
            </Box>
            <Box>
              <Typography sx={labelStyle}>Monitoring Person</Typography>
              <Input
                name="monitoringPersonName"
                control={control}
                errors={errors}
                label=""
                placeholder="Name"
                rules={{ required: "Person name is required" }}
              />
            </Box>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: 2,
              mb: 3,
            }}
          >
            <Box>
              <Typography sx={labelStyle}>Designation</Typography>
              <Input
                name="monitoringPersonDesignation"
                control={control}
                errors={errors}
                label=""
                placeholder="Designation"
                rules={{ required: "Designation is required" }}
              />
            </Box>
            <Box>
              <Typography sx={labelStyle}>Upload Report</Typography>
              <FileUpload
                name="monitoringDocument"
                control={control}
                setValue={setValue}
                getValues={getValues}
                setError={setError}
                clearErrors={clearErrors}
                trigger={trigger}
                label=""
                multiple={false}
                maxLimit={1}
                accept="application/pdf,.xlsx,.csv,.doc,.docx"
              />
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
              mb: 3,
            }}
          >
            <Button text="Preview" type="submit" variant="outlined" />
            <Button
              text="Submit Report"
              type="button"
              onClick={handleSubmit(() => onSubmit())}
              loading={submitting}
            />
          </Box>
        </form>

        {/* Submitted Reports Table */}
        {visitsLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress />
          </Box>
        ) : visits.length > 0 ? (
          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f0f0f0" }}>
                  <TableCell sx={headerCellStyle}>Period</TableCell>
                  <TableCell sx={headerCellStyle}>Date of Monitoring</TableCell>
                  <TableCell sx={headerCellStyle}>Personnel</TableCell>
                  <TableCell sx={headerCellStyle}>Designation</TableCell>
                  <TableCell sx={headerCellStyle}>User Name</TableCell>
                  <TableCell sx={headerCellStyle}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visits.map((visit: any) => (
                  <TableRow key={visit.id}>
                    <TableCell>
                      {formatPeriod(visit.periodFrom, visit.periodTo)}
                    </TableCell>
                    <TableCell>{formatDate(visit.monitoringDate)}</TableCell>
                    <TableCell>{visit.monitoringPersonName}</TableCell>
                    <TableCell>{visit.monitoringPersonDesignation}</TableCell>
                    <TableCell>{visit.user?.firstName || "-"}</TableCell>
                    <TableCell>
                      <Typography
                        component="span"
                        onClick={() => handleOpenModal(visit)}
                        sx={{
                          color: "#1b4332",
                          fontWeight: 600,
                          textDecoration: "underline",
                          cursor: "pointer",
                        }}
                      >
                        View Report & Remarks
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : null}
      </Box>

      {/* Report Viewer Modal */}
      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: "90vw",
            height: "85vh",
            maxWidth: "90vw",
            borderRadius: "8px",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            py: 1.5,
            px: 2,
            borderBottom: "1px solid #e0e0e0",
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: "1.1rem" }}>
            Monitoring Report Viewer -{" "}
            {selectedVisit
              ? formatPeriod(selectedVisit.periodFrom, selectedVisit.periodTo)
              : ""}
          </Typography>
          <IconButton onClick={handleCloseModal} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0, display: "flex", height: "100%" }}>
          {/* Left: Document Viewer */}
          <Box
            sx={{
              flex: "1 1 60%",
              bgcolor: "#e0e0e0",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
            }}
          >
            {selectedVisit?.monitoringDocument && documentUrl ? (
              <iframe
                src={documentUrl}
                title="Monitoring Report"
                style={{
                  width: "100%",
                  height: "100%",
                  border: "none",
                }}
              />
            ) : (
              <Box sx={{ textAlign: "center", color: "#666" }}>
                <Typography variant="h6">
                  {selectedVisit?.monitoringDocument
                    ? "Loading document..."
                    : "No document uploaded"}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Right: Remarks & Audit Trail */}
          <Box
            sx={{
              flex: "1 1 40%",
              display: "flex",
              flexDirection: "column",
              borderLeft: "1px solid #e0e0e0",
              height: "100%",
            }}
          >
            {/* Header */}
            <Box
              sx={{
                px: 2,
                py: 1.5,
                borderBottom: "1px solid #e0e0e0",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  bgcolor: "#1b4332",
                  borderRadius: "3px",
                }}
              />
              <Typography sx={{ fontWeight: 700, fontSize: "1rem" }}>
                Remarks & Audit Trail
              </Typography>
            </Box>

            {/* Remarks List */}
            <Box
              sx={{
                flex: 1,
                overflowY: "auto",
                px: 2,
                py: 1,
              }}
            >
              {remarksLoading ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    py: 3,
                  }}
                >
                  <CircularProgress size={24} />
                </Box>
              ) : remarks.length > 0 ? (
                remarks.map((remark: any) => (
                  <Box
                    key={remark.id}
                    sx={{
                      mb: 2,
                      p: 1.5,
                      border: "1px solid #e0e0e0",
                      borderRadius: "6px",
                      borderLeft: "3px solid",
                      borderLeftColor:
                        remark.user?.userType === "ADMIN"
                          ? "#b71c1c"
                          : remark.user?.userType === "INDIVIDUAL"
                          ? "#c87533"
                          : remark.user?.userType === "ORGANIZATION"
                          ? "#1b4332"
                          : "#1565c0",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 0.5,
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: "0.85rem",
                          color:
                            remark.user?.userType === "ADMIN"
                              ? "#b71c1c"
                              : remark.user?.userType === "INDIVIDUAL"
                              ? "#c87533"
                              : remark.user?.userType === "ORGANIZATION"
                              ? "#1b4332"
                              : "#1565c0",
                        }}
                      >
                        {remark.user?.userType === "ADMIN"
                          ? `ADMIN (${remark.user?.firstName || ""})`
                          : remark.user?.userType === "INDIVIDUAL"
                          ? `SNO (${remark.user?.firstName || ""})`
                          : remark.user?.userType === "ORGANIZATION"
                          ? `ENTITY (${remark.user?.firstName || ""})`
                          : `IO (${remark.user?.firstName || ""})`}
                      </Typography>
                      <Typography sx={{ fontSize: "0.75rem", color: "#888" }}>
                        {remark.created_at
                          ? new Date(remark.created_at).toLocaleString(
                              "en-IN",
                              {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                                // hour: "2-digit",
                                // minute: "2-digit",
                                // hour12: true,
                              }
                            )
                          : ""}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: "0.9rem", color: "#333" }}>
                      {remark.remarks}
                    </Typography>
                  </Box>
                ))
              ) : (
                <Box sx={{ textAlign: "center", py: 3, color: "#999" }}>
                  <Typography>No remarks yet</Typography>
                </Box>
              )}
            </Box>

            {/* Remark Input */}
            <Box
              sx={{
                px: 2,
                py: 1.5,
                borderTop: "1px solid #e0e0e0",
                display: "flex",
                gap: 1,
                alignItems: "center",
              }}
            >
              <TextField
                value={remarkText}
                onChange={(e) => setRemarkText(e.target.value)}
                placeholder="Type your remark here..."
                size="small"
                fullWidth
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handlePostRemark();
                  }
                }}
              />
              <Button
                text="Post"
                onClick={handlePostRemark}
                loading={postingRemark}
              />
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </DashboardWrapper>
  );
};

export default MonitoringReport;
